import { useEffect, useState } from 'react';
import {
    EditableResourceType,
    EditorDto,
    useEndEditingSessionMutation,
    useSendEditingHeartbeatMutation,
} from 'store/api/editingSession';

const HEARTBEAT_INTERVAL_MS = 1000;

interface UseResourceEditingSessionProps {
    resourceType: EditableResourceType;
    resourceId: string | number | undefined;
    enabled?: boolean;
}

interface UseResourceEditingSessionResult {
    otherEditors: EditorDto[];
    isLoading: boolean;
}

export const useResourceEditingSession = ({
    resourceType,
    resourceId,
    enabled = true,
}: UseResourceEditingSessionProps): UseResourceEditingSessionResult => {
    const [otherEditors, setOtherEditors] = useState<EditorDto[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const [sendHeartbeat] = useSendEditingHeartbeatMutation();
    const [endSession] = useEndEditingSessionMutation();

    useEffect(() => {
        if (!enabled || !resourceId) {
            setIsLoading(false);
            return;
        }

        const resource = { resourceType, resourceId };
        // A heartbeat still in flight when the session moves to another resource must not report its editors.
        let isCurrentSession = true;

        const sendHeartbeatRequest = async () => {
            try {
                const response = await sendHeartbeat(resource).unwrap();
                if (isCurrentSession) {
                    setOtherEditors(response.otherEditors);
                }
            } catch (error) {
                console.error('Failed to send editing session heartbeat:', error);
            } finally {
                if (isCurrentSession) {
                    setIsLoading(false);
                }
            }
        };

        void sendHeartbeatRequest();

        const interval = setInterval(() => {
            void sendHeartbeatRequest();
        }, HEARTBEAT_INTERVAL_MS);

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void sendHeartbeatRequest();
            }
        };

        const handleBeforeUnload = () => {
            navigator.sendBeacon(
                `${import.meta.env.VITE_API_URL}/v1/editing-session/${resourceType}/${resourceId}`,
                JSON.stringify(resource)
            );
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            isCurrentSession = false;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            endSession(resource)
                .unwrap()
                .catch((error: unknown) => console.error('Failed to end editing session:', error));
        };
    }, [enabled, resourceType, resourceId, sendHeartbeat, endSession]);

    return {
        otherEditors,
        isLoading,
    };
};
